import { Module } from "vuex";
import { AxiosResponse } from "axios";
import { SET_USERS, SET_USER, SET_ERRORMSG, SET_USER_STATS } from "@/store/mutation-types";
import {
  REGISTER_USER,
  GET_USER,
  GET_USERS,
  CLEAR_USERS_ISLOADING,
  EDIT_USER,
  TOGGLE_USER_IS_ACTIVE,
  DELETE_USER,
  GET_USER_STATS,
  RECOVER_USER_ACCOUNT,
} from "@/store/action-types";
import { StateObject } from "@/dtos/State.dto";
import User from "@/services/user";
import { EditUser, GetUser, RegisterUser } from "@/dtos/User.dto";

const user: Module<any, {}> = {
  state: {
    user: {
      data: null,
      isLoading: false,
    },
    userStats: {
      data: null,
      isLoading: false,
    },
    users: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    actingUser: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    user: (state: { user: any }) => state.user,
    userStats: (state: { userStats: any }) => state.userStats,
    actingUser: (state: { actingUser: any }) => state.actingUser,
    users: (state: { users: any }) => state.users,
  },
  mutations: {
    [SET_USER]: (
      state: { user: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.user.data = payload.data;
      state.user.isLoading = payload.isLoading;
    },
    [SET_USER_STATS]: (
      state: { userStats: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.userStats.data = payload.data;
      state.userStats.isLoading = payload.isLoading;
    },
    [SET_USERS]: (
      state: { users: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.users.data = payload.data;
      if (payload.metaData != null) state.users.metaData = payload.metaData;
      state.users.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_USER]: async ({ commit }: any, payload: any) => {
      commit(SET_USER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await User.registerUser(payload as RegisterUser);

        if (result) {
          if (result.data.status) {
            commit(SET_USER, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_USER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to register user");
        }
        return false;
      }
    },
    [GET_USER]: async ({ commit }: any, payload: any) => {
      commit(SET_USER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await User.getUsers(payload as GetUser);

        if (result) {
          if (result.data.status) {
            commit(SET_USER, { data: result.data.data[0], isLoading: false });
            return true;
          }
          commit(SET_USER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load user");
        }
        return false;
      }
    },
    [GET_USERS]: async ({ commit }: any, payload: any) => {
      commit(SET_USERS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await User.getUsers(payload as GetUser);

        if (result) {
          if (result.data.status) {
            commit(SET_USERS, {
              data: result.data.data,
              metaData: result.data.metaData,
              isLoading: false,
            });
            return true;
          }
          commit(SET_USERS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USERS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USERS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load users");
        }
        return false;
      }
    },
    [GET_USER_STATS]: async ({ commit }: any, payload: any) => {
      commit(SET_USER_STATS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await User.getUserStats(payload as GetUser);

        if (result) {
          if (result.data.status) {
            commit(SET_USER_STATS, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_USER_STATS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER_STATS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER_STATS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load user stats");
        }
        return false;
      }
    },
    [EDIT_USER]: async ({ commit }: any, payload: FormData) => {
      commit(SET_USER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await User.editUser(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_USER, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_USER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit user");
        }
        return false;
      }
    },
    [TOGGLE_USER_IS_ACTIVE]: async ({ commit, state }: any, payload: EditUser) => {
      commit(SET_USER, { isLoading: true });

      try {
        const result: AxiosResponse = await User.toggleUserIsActive(payload as EditUser);

        if (result) {
          if (result.data.status) {
            let index;
            if (state.users.data) {
              state.users.data.forEach((e: any, i: number) => {
                if (e.id == result.data.data.id) {
                  index = i;
                }
              });
              if (index != null && index !== undefined)
                state.users.data[index].isActive = result.data.data.isActive;
            }

            commit(SET_USER, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_USER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to toggle user status");
        }
        return false;
      }
    },
    [RECOVER_USER_ACCOUNT]: async ({ commit, state }: any, payload: EditUser) => {
      commit(SET_USER, { isLoading: true });

      try {
        const result: AxiosResponse = await User.recoverUserAccount(payload as EditUser);

        if (result) {
          if (result.data.status) {
            let index;
            if (state.users.data) {
              state.users.data.forEach((e: any, i: number) => {
                if (e.id == result.data.data.id) {
                  index = i;
                }
              });
              if (index != null && index !== undefined) {
                state.users.data[index].isActive = result.data.data.isActive;
                state.users.data[index].isDeleted = result.data.data.isDeleted;
              }
            }

            commit(SET_USER, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_USER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to recover user account");
        }
        return false;
      }
    },
    [DELETE_USER]: async ({ commit }: any, payload: string) => {
      commit(SET_USER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await User.deleteUser(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_USER, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_USER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_USER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_USER, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete user");
        }
        return false;
      }
    },
    [CLEAR_USERS_ISLOADING]: ({ commit }) => {
      commit(SET_USER, { isLoading: false });
      commit(SET_USERS, { isLoading: false });
    },
  },
};

export default user;
